// Amenity types selectable for the dashboard's "nearest amenity" sort.
//
// Two data sources, mirrored server-side in web/lib/ranking-query.js
// (NEAREST_CATEGORIES):
//   - dog categories  → statewide dog_amenities DB (haversine; accurate at any
//                        distance, so every property always has a nearest one)
//   - walk categories → that property's own walkability scan
//                        (walkability_venues.distance_mi; ~1mi reach, and only
//                        for scanned properties → distance is null beyond that)
//
// The labels here drive the SortSelect "Nearest amenity" group and the
// PropertyCard distance metric line.

export type NearestCategory =
  | 'dog_park'
  | 'beach'
  | 'daycare'
  | 'bar'
  | 'coffee_shop'
  | 'restaurant'
  | 'convenience_store';

export interface NearestCategoryMeta {
  value: NearestCategory;
  /** Display label, e.g. "Coffee Shop". Used in the dropdown + card metric. */
  label: string;
  /** Which data source backs this category's distance. */
  source: 'dog' | 'walk';
}

export const NEAREST_CATEGORIES: readonly NearestCategoryMeta[] = [
  { value: 'dog_park', label: 'Dog Park', source: 'dog' },
  { value: 'beach', label: 'Dog Beach', source: 'dog' },
  { value: 'daycare', label: 'Daycare', source: 'dog' },
  { value: 'bar', label: 'Dog Bar', source: 'dog' },
  { value: 'coffee_shop', label: 'Coffee Shop', source: 'walk' },
  { value: 'restaurant', label: 'Restaurant', source: 'walk' },
  { value: 'convenience_store', label: 'Convenience Store', source: 'walk' },
] as const;

/** Valid `nearestCat` values — used for URL validation. */
export const NEAREST_CATEGORY_VALUES: readonly NearestCategory[] =
  NEAREST_CATEGORIES.map((c) => c.value);

/** Lookup: category value → metadata (label, source). */
export const NEAREST_CATEGORY_BY_VALUE: Record<NearestCategory, NearestCategoryMeta> =
  NEAREST_CATEGORIES.reduce(
    (acc, c) => {
      acc[c.value] = c;
      return acc;
    },
    {} as Record<NearestCategory, NearestCategoryMeta>,
  );
